import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { Component, Fragment, Suspense, lazy, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { ThemeProvider } from "./contexts/ThemeContext";
import { Toaster } from "sonner";
import useDisableHoverOnScroll from "./hooks/useDisableHoverOnScroll.js";
import { fetchUserInfo } from "./store/slices/user/asyncThunk.js";
import {
  selectLoggedIn,
  selectRegionBlocked,
  setLoggedIn,
  setAuthLoading,
  setRegionBlocked,
} from "./store/slices/auth/slice.js";
import VerifyEmailPage from "./screens/Verify/VerifyEmail.js";
import TermsPage from "./screens/TermsPage/TermsPage.js";
import SubmitARequestPage from "./screens/SubmitARequestPage/SubmitARequestPage.js";
import RequestRemovalPage from "./screens/RequestRemovalPage/RequestRemovalPage.js";
import PrivacyPage from "./screens/PrivacyPage/PrivacyPage.js";
import DeleteAccountPage from "./screens/DeleteAccountPage/DeleteAccountPage.js";
import HomePage from "./screens/HomePage/HomePage.js";
import SubscriptionPage from "./screens/SubscriptionPage/SubscriptionPage.js";
import RewardsTemplatePage from "./screens/RewardsTemplatePage/RewardsTemplatePage.js";
import TrainingPage from "./screens/TrainingPage/TrainingPage.js";
import TrainTutorialPage from "./screens/TrainTutorialPage/TrainTutorialPage.js";
import ReferralEarningsPage from "./screens/ReferralEarningsPage/ReferralEarningsPage.js";
import CollectionPage from "./screens/CollectionPage/CollectionPage.js";
import TrainPage from "./screens/TrainPage/TrainPage.js";
import TrainingCompletedPage from "./screens/TrainingCompletedPage/TrainingCompletedPage.js";
import CollectionDetailsPage from "./screens/CollectionDetailsPage/CollectionDetailsPage.js";
import CreateImageDetailsPage from "./screens/CreateImageDetailsPage/CreateImageDetailsPage.js";
import GlobalStyles from "./styles/globalStyles";
import "./styles/index.css";

import ProtectedRoute from "./components/utils/ProtectedRoute.js";
import TrainingSelectCoverPage from "./screens/TrainingSelectCoverPage/TrainingSelectCoverPage.js";
import TelegramTutorialPage from "./screens/TutorialsPages/TelegramTutorialPage.js";
import CryptoTutorialPage from "./screens/TutorialsPages/CryptoTutorialPage.js";
import WalletPage from "./screens/WalletPage/WalletPage.js";
import { getOrCreateBrowserId } from "./utils/utils.js";
import { selectUser } from "./store/slices/user/slice.js";
import { VIDEO_RESOLUTION } from "./utils/constants.js";
import EditTrainingModelPage from "./screens/EditTrainingModelPage/EditTrainingModelPage.js";
import PlaylistPage from "./screens/PlaylistPage/PlaylistPage.js";
import PlaylistDetailPage from "./screens/PlaylistDetailPage/PlaylistDetailPage.js";
import SpinWheelPage from "./screens/SpinWheelPage/SpinWheelPage.js";
import { ensureTelegramWidgetHost } from "./utils/telegramWidget.js";
import ProfilePage from "./screens/ProfilePage/ProfilePage.js";
import EditProfilePage from "./screens/EditProfilePage/EditProfilePage.js";
import { lineSpinner, pulsar, ring } from "ldrs";
import "ldrs/react/Spiral.css";
import { fetchSubscriptions } from "./store/slices/subscription/asyncThunks.js";
import NotAvailablePage from "./screens/NotAvailablePage/NotAvailablePage.js";
import TabsLayout from "./components/TabsLayout/TabsLayout.js";
import LandingPromoPopup from "./components/LandingPromoPopup/LandingPromoPopup.js";
ring.register();
pulsar.register();
lineSpinner.register();

const CHUNK_LOAD_ERROR =
  /Loading chunk \d+ failed|Failed to fetch dynamically imported module|Importing a module script failed/i;

const lazyWithRetry = (load, key) =>
  lazy(() =>
    load().catch(error => {
      const message = String(error?.message || error || "");
      if (
        typeof window !== "undefined" &&
        (error?.name === "ChunkLoadError" || CHUNK_LOAD_ERROR.test(message))
      ) {
        try {
          const retryKey = `lazy-route-retry:${key}`;
          if (window.sessionStorage.getItem(retryKey) !== "1") {
            window.sessionStorage.setItem(retryKey, "1");
            window.location.reload();
            return new Promise(() => {});
          }
        } catch {
          /* fall through to the visible route error */
        }
      }
      throw error;
    })
  );

class RouteErrorBoundary extends Component {
  state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidUpdate(prevProps) {
    if (prevProps.locationKey !== this.props.locationKey && this.state.hasError) {
      this.setState({ hasError: false });
    }
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error("[route] render failed", error, info);
  }

  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div style={{ minHeight: "60vh", display: "grid", placeItems: "center", padding: 24, color: "#fff" }}>
        <div style={{ display: "grid", gap: 12, justifyItems: "center", textAlign: "center" }}>
          <p style={{ margin: 0 }}>This game failed to load.</p>
          <button type="button" onClick={() => window.location.reload()}>
            Refresh
          </button>
        </div>
      </div>
    );
  }
}

// Games routes are code-split: none of their JS (THREE.js for the 3D
// dice, framer-motion game boards, ...) lands in the main bundle, and
// each game page loads its own chunk on first visit.
const CoinflipPage = lazyWithRetry(() => import("./screens/games/CoinflipPage/CoinflipPage.js"), "coinflip");
const DicePage = lazyWithRetry(() => import("./screens/games/DicePage/DicePage.js"), "dice");
const RollDicePage = lazyWithRetry(() => import("./screens/games/RollDicePage/RollDicePage.js"), "lucky-dice");
const ScratchPage = lazyWithRetry(() => import("./screens/games/ScratchPage/ScratchPage.js"), "scratch");
const SpinPage = lazyWithRetry(() => import("./screens/games/SpinPage/SpinPage.js"), "spin");
const DropPage = lazyWithRetry(() => import("./screens/games/DropPage/DropPage.js"), "drop");
const MinesPage = lazyWithRetry(() => import("./screens/games/MinesPage/MinesPage.js"), "mines");
const SlotsPage = lazyWithRetry(() => import("./screens/games/SlotsPage/SlotsPage.js"), "slots");
const BunnyCrashPage = lazyWithRetry(() => import("./screens/games/BunnyCrashPage/BunnyCrashPage.js"), "bunny-crash");
const GamesPage = lazyWithRetry(() => import("./screens/games/GamesPage/GamesPage.js"), "games");

const AppContent = () => {
  const dispatch = useDispatch();
  const loggedIn = useSelector(selectLoggedIn);
  const regionBlocked = useSelector(selectRegionBlocked);
  const user = useSelector(selectUser);
  const location = useLocation();
  const navigate = useNavigate();
  const [isLandingPromoOpen, setIsLandingPromoOpen] = useState(false);
  useDisableHoverOnScroll();

  useEffect(() => {
    if (loggedIn === null) {
      dispatch(fetchUserInfo())
        .unwrap()
        .then(() => {})
        .catch((err) => {
          if (err?.status === 451) {
            dispatch(setRegionBlocked(true));
          }
          dispatch(setLoggedIn({ loggedIn: false }));
        })
        .finally(() => {
          dispatch(setAuthLoading(false));
        });
    }
  }, [dispatch, loggedIn]);

  // Opening any game (or the games lobby) starts at the top of the page —
  // the router otherwise keeps the previous route's scroll position.
  useEffect(() => {
    if (location.pathname.startsWith("/games")) {
      window.scrollTo(0, 0);
    }
  }, [location.pathname]);

  useEffect(() => {
    if (regionBlocked && location.pathname !== "/not-available") {
      navigate("/not-available", { replace: true });
    }
  }, [regionBlocked, location.pathname, navigate]);

  useEffect(() => {
    getOrCreateBrowserId();
  }, []);

  useEffect(() => {
    ensureTelegramWidgetHost();
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (location.pathname !== "/" || regionBlocked || loggedIn !== false) {
      setIsLandingPromoOpen(false);
      return;
    }

    let hasSeenLandingPromo = false;
    try {
      hasSeenLandingPromo =
        window.sessionStorage.getItem("photoVideoLandingPromoSeenV2") === "1";
    } catch {
      hasSeenLandingPromo = false;
    }

    if (!hasSeenLandingPromo) {
      setIsLandingPromoOpen(true);
    }
  }, [location.pathname, regionBlocked, loggedIn]);

  const closeLandingPromo = () => {
    setIsLandingPromoOpen(false);
    try {
      window.sessionStorage.setItem("photoVideoLandingPromoSeenV2", "1");
    } catch {
      /* storage can be unavailable in private contexts */
    }
  };

  useEffect(() => {
    if (loggedIn) {
      if (
        user?.subscription &&
        user?.subscription?.name &&
        user?.subscription?.endDate &&
        new Date(user?.subscription?.endDate) > new Date()
      ) {
        const resolution = localStorage.getItem("videoResolution");
        const fps = localStorage.getItem("videoFps");
        const settingsAlreadyReset = localStorage.getItem(
          "settingsAlreadyReset",
        );

        if (!settingsAlreadyReset) {
          if (!resolution || resolution === VIDEO_RESOLUTION.LOW) {
            localStorage.setItem("videoResolution", VIDEO_RESOLUTION.MEDIUM);
          }

          if (!fps || parseInt(fps) === 30) {
            localStorage.setItem("videoFps", 45);
          }
          localStorage.setItem("settingsAlreadyReset", true);
        }
      }
    }
  }, [loggedIn, user]);

  useEffect(() => {
    dispatch(fetchSubscriptions())
      .unwrap()
      .then(() => {})
      .catch((err) => console.log(err.message));
  }, [dispatch]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    const handleWheel = (event) => {
      if (event.ctrlKey) {
        event.preventDefault();
      }
    };

    const handleKeyDown = (event) => {
      const zoomKeys = ["=", "+", "-", "_"];
      if ((event.ctrlKey || event.metaKey) && zoomKeys.includes(event.key)) {
        event.preventDefault();
      }
      if ((event.ctrlKey || event.metaKey) && event.key === "0") {
        event.preventDefault();
      }
    };

    const handleTouchMove = (event) => {
      if (event.scale && event.scale !== 1) {
        event.preventDefault();
      }
      if (event.touches && event.touches.length > 1) {
        event.preventDefault();
      }
    };

    let lastTouchEnd = 0;
    const handleTouchEnd = (event) => {
      if (event.target?.closest?.("[data-allow-rapid-touch]")) {
        lastTouchEnd = Date.now();
        return;
      }

      const now = Date.now();
      if (now - lastTouchEnd <= 300) {
        event.preventDefault();
      }
      lastTouchEnd = now;
    };

    const preventGesture = (event) => {
      event.preventDefault();
    };

    document.addEventListener("wheel", handleWheel, { passive: false });
    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("touchmove", handleTouchMove, { passive: false });
    document.addEventListener("touchend", handleTouchEnd, { passive: false });
    window.addEventListener("gesturestart", preventGesture, { passive: false });
    window.addEventListener("gesturechange", preventGesture, {
      passive: false,
    });
    window.addEventListener("gestureend", preventGesture, { passive: false });

    return () => {
      document.removeEventListener("wheel", handleWheel);
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("touchmove", handleTouchMove);
      document.removeEventListener("touchend", handleTouchEnd);
      window.removeEventListener("gesturestart", preventGesture);
      window.removeEventListener("gesturechange", preventGesture);
      window.removeEventListener("gestureend", preventGesture);
    };
  }, []);

  // useEffect(() => { ???
  //   const params = new URLSearchParams(location.search);
  //   const ref = params.get ("ref");

  //   if (!loggedIn) {
  //     if (ref) {
  //       dispatch(setAffiliateRef(ref));
  //       localStorage.setItem("affiliateRef", ref);

  //       dispatch(setSignUpModalOpen(true));
  //     } else {
  //       const savedRef = localStorage.getItem("affiliateRef");
  //       if (savedRef) {
  //         dispatch(setAffiliateRef(savedRef));
  //       } else {
  //         dispatch(setSignUpModalOpen(false));
  //         dispatch(setAffiliateRef(null));
  //       }
  //     }
  //   } else {
  //     if (ref) {
  //       params.delete("ref");
  //       navigate({ search: params.toString() }, { replace: true });

  //       dispatch(setAffiliateRef(null));
  //       localStorage.removeItem("affiliateRef");
  //     }
  //   }
  // }, [dispatch, location.pathname, location.search, loggedIn, navigate]);
  return (
    <Fragment>
      <GlobalStyles />

      <RouteErrorBoundary locationKey={location.pathname}>
        <Suspense
          fallback={
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minHeight: "60vh",
              }}
            >
              <l-line-spinner size="28" stroke="1.5" speed="1.5" color="white" />
            </div>
          }
        >
      <Routes>
        <Route element={<TabsLayout />}>
          <Route path="/" element={<HomePage />} />
          <Route path="/collection" element={<CollectionPage />} />
          <Route path="/train" element={<TrainPage />} />
          <Route path="/games" element={<GamesPage />} />
          <Route path="/games/coinflip" element={<CoinflipPage />} />
          <Route path="/games/dice" element={<DicePage />} />
          <Route path="/games/lucky-dice" element={<RollDicePage />} />
          <Route path="/games/scratch" element={<ScratchPage />} />
          <Route path="/games/spin" element={<SpinPage />} />
          <Route path="/games/drop" element={<DropPage />} />
          <Route path="/games/mines" element={<MinesPage />} />
          <Route path="/games/slots" element={<SlotsPage />} />
          <Route path="/games/bunny-crash" element={<BunnyCrashPage />} />
        </Route>
        <Route path="/template/:id" element={<CollectionDetailsPage />} />
        <Route
          path="/create-image"
          element={
            <ProtectedRoute>
              <CreateImageDetailsPage />
            </ProtectedRoute>
          }
        />
        <Route path="/playlist" element={<PlaylistPage />} />
        <Route path="/playlist-detail/:id" element={<PlaylistDetailPage />} />
        <Route path="/subscription" element={<SubscriptionPage />} />
        <Route path="/verify-email/:tokenId" element={<VerifyEmailPage />} />
        <Route path="/account/delete" element={<DeleteAccountPage />} />
        <Route path="/terms-and-conditions" element={<TermsPage />} />
        <Route path="/privacy-policy" element={<PrivacyPage />} />
        <Route path="/submit-a-request" element={<SubmitARequestPage />} />
        <Route
          path="/request-removal-of-content"
          element={<RequestRemovalPage />}
        />

        <Route path="/telegram-tutorial" element={<TelegramTutorialPage />} />
        <Route path="/crypto-tutorial" element={<CryptoTutorialPage />} />
        <Route path="/spin" element={<SpinWheelPage />} />
        <Route path="/profile" element={<ProfilePage />} />
        <Route path="/profile/:username" element={<ProfilePage />} />
        <Route path="/edit-profile" element={<EditProfilePage />} />

        <Route path="/not-available" element={<NotAvailablePage />} />

        {/* Protected Routes */}
        <Route path="/wallet" element={<WalletPage />} />
        <Route
          path="/rewards/template"
          element={
            <ProtectedRoute>
              <RewardsTemplatePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/referral/earnings"
          element={
            <ProtectedRoute>
              <ReferralEarningsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/train/:modelId"
          element={
            <ProtectedRoute>
              <TrainingPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/train/select-cover/:modelId"
          element={<TrainingSelectCoverPage />}
        />
        <Route
          path="/train/completed/:modelId"
          element={
            <ProtectedRoute>
              <TrainingCompletedPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/train/edit/:modelId"
          element={
            <ProtectedRoute>
              <EditTrainingModelPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/train-tutorial"
          element={
            <ProtectedRoute>
              <TrainTutorialPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/account/delete"
          element={
            <ProtectedRoute>
              <DeleteAccountPage />
            </ProtectedRoute>
          }
        />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
        </Suspense>
      </RouteErrorBoundary>

      <LandingPromoPopup
        isOpen={isLandingPromoOpen}
        onClose={closeLandingPromo}
      />

      <Toaster
        position="bottom-right"
        closeButton={false}
        richColors
        toastOptions={{
          style: {
            borderRadius: 24,
            border: "1px solid #142743",
            background: "#0a1424",
            color: "white",
          },
        }}
      />
    </Fragment>
  );
};

function App() {
  return (
    <ThemeProvider>
      <GoogleOAuthProvider clientId="473538975839-94fjan8hhqdg7fig4fiqc9nqgp7um7ai.apps.googleusercontent.com">
        <AppContent />
      </GoogleOAuthProvider>
    </ThemeProvider>
  );
}

export default App;
